<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  #trainingHubSidebarButton {
    align-items: center;
    border: none;
    margin-left: utils.in_rem(4px);
    padding-inline: 0;
    width: fit-content;
    background: none;
    cursor: pointer;
    display: flex;
    gap: utils.in_rem(8px);
    outline: none;
    position: relative;
    justify-content: center;

    &:hover #progress,
    &:focus #progress,
    &:active #progress,
    #progress.active {
      @include colors.background-color(interfaceTransparentWhite_15);
      outline: 4px solid colors.get(interfaceTransparentWhite_15);
    }

    &:hover .name,
    &:focus #progress .name,
    &:active #progress .name,
    .name.active {
      @include colors.background-color(interfaceTransparentWhite_15);
    }

    .name {
      @include colors.color(interfaceTransparentWhite_90);
      @include mixins.sidebar();
      height: utils.in_rem(32px);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: utils.in_rem(8px);
      border-radius: utils.in_rem(16px);
      white-space: nowrap;
    }

    #trainingHubSidebarDialog {
      @keyframes zoom {
        from {
          transform: scale(0.95);
        }
        to {
          transform: scale(1);
        }
      }

      width: 23.75rem;
      animation: zoom 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      background: white;
      bottom: 0;
      border-radius: 0.5rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      cursor: default;
      display: grid;
      gap: 0.5rem;
      overflow: hidden;
      padding: 0.5rem;
      position: fixed;
      text-align: left;
    }
  }

  $borderWidth: 3px;
  $animationTime: 0.3s;
  $border-color-default: rgba(255, 255, 255, 0.8);
  $border-color-fill: #46d7a1;
  $size: 2.5rem;
  $howManySteps: 100;

  #progress {
    width: $size;
    height: $size;
    align-items: center;
    border-radius: 50%;
    position: relative;
    display: flex;
    justify-content: center;

    &:after {
      content: "";
      width: 100%;
      height: 100%;
      border-radius: 50%;
      border: $borderWidth solid $border-color-default;
      position: absolute;
    }

    > span {
      width: 50%;
      height: 100%;
      overflow: hidden;
      position: absolute;
      z-index: 1;
    }

    #progressLeft,
    #progressRight {
      #progressBar {
        width: 100%;
        height: 100%;
        background: none;
        border-width: $borderWidth;
        border-style: solid;
        position: absolute;
        top: 0;
        border-color: $border-color-fill;
      }
    }

    #progressLeft {
      left: 0;

      #progressBar {
        left: 100%;
        border-top-right-radius: calc($size / 2);
        border-bottom-right-radius: calc($size / 2);
        border-left: 0;
        transform-origin: center left;
      }
    }

    #progressRight {
      right: 0;

      #progressBar {
        left: -100%;
        border-top-left-radius: calc($size / 2);
        border-bottom-left-radius: calc($size / 2);
        border-right: 0;
        transform-origin: center right;
      }
    }

    #progressValue {
      font-size: 0.8rem;
      color: white;
      position: relative;

      > span {
        font-size: 10px;
        font-weight: 700;
        line-height: 120%;
        text-transform: uppercase;
        animation: fadeIn 0.3s ease-in-out forwards;
      }
    }
  }

  @for $i from 1 through $howManySteps {
    $stepName: ($i * calc(100 / $howManySteps));
    $halfSteps: calc($howManySteps / 2);

    #progress[data-percentage="#{$stepName}"] {
      #progressRight #progressBar {
        animation: loading-#{min($i, $halfSteps)}
          $animationTime
          linear
          forwards;
      }
      #progressLeft #progressBar {
        @if $i > $halfSteps {
          animation: loading-#{$i -
            $halfSteps}
            $animationTime
            linear
            forwards
            $animationTime;
        } @else {
          animation: none;
        }
      }
    }
  }

  @for $i from 1 through calc($howManySteps / 2) {
    $degrees: calc(180 / calc($howManySteps / 2) * $i);
    @keyframes loading-#{$i} {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(#{$degrees}deg);
      }
    }
  }
</style>

<script>
  import { trainingHubTaskStore } from "../../../../api-stores/training-hub-store";
  import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
  import { currentPath } from "../../../elements/Sidebar/stores/current-path-store";
  import { sidebarIsExpanded } from "../../../elements/Sidebar/stores/sidebar-store.js";
  import SkeletonLoader from "../Components/SkeletonLoader.svelte";
  import DialogContent from "./DialogContent.svelte";
  import { showDialog } from "./stores";

  $: shouldShow = $userStore?.trainingHubStatus === "inProgress";

  $: active = $currentPath === "/training";

  let hideTimer;

  const showSidebar = () => {
    clearTimeout(hideTimer);
    $showDialog = true;
  };

  const hideSidebar = () => {
    hideTimer = setTimeout(() => {
      $showDialog = false;
    }, 500);
  };

  let elButton;
  let elDialog;

  $: if (!$sidebarIsExpanded && elButton) {
    elButton.blur();
  }

  $: iconAria = $showDialog
    ? {
        "aria-controls": "trainingHubSidebarDialog",
      }
    : {};

  // use a fixed position to stop being hidden (dialog overflows sidebar)
  $: if (elButton && elDialog) {
    const rect = elButton.getBoundingClientRect();
    elDialog.style.bottom = `${window.innerHeight - rect.bottom}px`;
    elDialog.style.left = `${rect.left + 186}px`;
  }

  trainingHubTaskStore.once();
  const { meta } = trainingHubTaskStore;
  $: completedTasks = $trainingHubTaskStore.totalProgress.completedTasks || 0;
  $: totalTasks = $trainingHubTaskStore.totalProgress.totalTasks || 0;
  $: completedPercent =
    totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);
</script>

{#if shouldShow}
  <button
    id="trainingHubSidebarButton"
    tabindex="0"
    aria-haspopup="dialog"
    aria-expanded={$showDialog}
    bind:this={elButton}
    {...iconAria}
    on:mouseenter={showSidebar}
    on:mouseleave={hideSidebar}
    class:$showDialog
  >
    <div id="progress" data-percentage={completedPercent} class:active>
      <span id="progressLeft">
        <span id="progressBar"></span>
      </span>
      <span id="progressRight">
        <span id="progressBar"></span>
      </span>
      <div id="progressValue">
        {#if !$meta.firstFetchDone}
          <SkeletonLoader />
        {:else}
          <span>{completedTasks}/{totalTasks}</span>
        {/if}
      </div>
    </div>
    {#if $sidebarIsExpanded}
      <span class="name" class:active>Onboarding</span>
    {/if}
    {#if $showDialog}
      <div
        id="trainingHubSidebarDialog"
        bind:this={elDialog}
        role="dialog"
        aria-labelledby="trainingHubSidebarButton"
      >
        <DialogContent />
      </div>
    {/if}
  </button>
{/if}
